import React from 'react';
import { Button, DatePicker, Form, FormInstance, Space } from 'antd';
import moment from 'moment';

interface GlobalMuteWindowPickerProps {
  form: FormInstance;
}

// antd's DatePicker types disabledDate against its default date lib; the project
// uses moment, so accept a loose type and treat it as a moment instance.
const disablePastDates = (date: any) => !!date && moment(date).isBefore(moment(), 'day');

const range = (start: number, end: number): number[] => {
  const result: number[] = [];
  for (let i = start; i < end; i += 1) result.push(i);
  return result;
};

// When the picked date is today, disable clock times that are already in the
// past. Hours before the current hour are blocked; within the current hour,
// minutes before the current minute are blocked. Other days are unrestricted
// (past days are already handled by disablePastDates).
const disablePastTime = (date: any) => {
  const now = moment();
  const picked = date ? moment(date) : null;
  if (!picked || !picked.isSame(now, 'day')) return {};
  return {
    disabledHours: () => range(0, now.hour()),
    disabledMinutes: (selectedHour: number) =>
      selectedHour === now.hour() ? range(0, now.minute()) : [],
  };
};

const showTime = { format: 'HH:mm' };

// The form disables antd's auto required-mark (which rendered the asterisk with
// an awkward gap); render it manually so it sits right next to the label.
const requiredLabel = (text: string) => (
  <span>
    {text}
    <span style={{ color: '#ff4d4f', marginLeft: 2 }}>*</span>
  </span>
);

// Keep the popup inside the drawer so a wide range popup doesn't spill past the
// drawer edge, and so it stays aligned when the drawer body scrolls.
const popupInParent = (node: HTMLElement) => (node.parentElement as HTMLElement) || document.body;

const GlobalMuteWindowPicker = ({ form }: GlobalMuteWindowPickerProps) => {
  const from = Form.useWatch('muteFrom', form) as moment.Moment | undefined;

  // Show the customer-timezone UTC offset (e.g. "UTC-04:00"), matching the
  // pulse-nav customer indicator. moment operates in the customer tz via the
  // global moment.tz.setDefault, so `.format('Z')` yields the DST-correct offset.
  const tzLabel = `UTC${(from || moment()).format('Z')}`;

  // Quick-select shortcuts always start "now"; the window runs for the duration.
  const applyPreset = (hours: number) => {
    const start = moment();
    form.setFieldsValue({ muteFrom: start, muteUntil: start.clone().add(hours, 'hour') });
  };

  return (
    <Form.Item label={`Mute Window (${tzLabel})`} style={{ marginBottom: 8 }}>
      <Space size={8} style={{ marginBottom: 12 }}>
        <Button size="small" onClick={() => applyPreset(2)}>
          Next 2 hours
        </Button>
        <Button size="small" onClick={() => applyPreset(4)}>
          Next 4 hours
        </Button>
      </Space>

      <Form.Item
        name="muteFrom"
        label={requiredLabel('Start')}
        rules={[{ required: true, message: 'Select a start time' }]}
        style={{ marginBottom: 12 }}
      >
        <DatePicker
          style={{ width: '100%' }}
          showTime={showTime}
          format="YYYY-MM-DD HH:mm"
          allowClear={false}
          disabledDate={disablePastDates}
          disabledTime={disablePastTime}
          getPopupContainer={popupInParent}
        />
      </Form.Item>

      <Form.Item
        name="muteUntil"
        label={requiredLabel('End')}
        dependencies={['muteFrom']}
        style={{ marginBottom: 0 }}
        rules={[
          { required: true, message: 'Select an end time' },
          ({ getFieldValue }) => ({
            validator(_, value: moment.Moment) {
              const start = getFieldValue('muteFrom') as moment.Moment | undefined;
              if (!value || !start) return Promise.resolve();
              if (!value.isAfter(start)) {
                return Promise.reject(new Error('End must be after start'));
              }
              if (value.diff(start, 'hours', true) > 24) {
                return Promise.reject(new Error('Window is capped at 24 hours'));
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <DatePicker
          style={{ width: '100%' }}
          showTime={showTime}
          format="YYYY-MM-DD HH:mm"
          allowClear={false}
          disabledDate={disablePastDates}
          disabledTime={disablePastTime}
          getPopupContainer={popupInParent}
        />
      </Form.Item>
    </Form.Item>
  );
};

export default GlobalMuteWindowPicker;
